import { useRef, useState, useEffect, useMemo } from 'react'
import { Container, Row, Col } from '@ui/wrapper'
import Accordion from '@ui/accordion'
import {
  SectionWrapper,
  StyledText,
  Card,
  CardText,
  CardHeader,
  CardFooter,
  Menu,
  MenuButton,
  MenuItem
} from './style'
import { PieChart, BarChart } from './charts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import './style.css'
import { Spinner } from 'react-bootstrap'
import MenuInfo from '@components/menu-info'
import DocumentArchive from '@components/document-archive/layout-01'
import { usePageContext } from '@context/PageContext'
import {
  texts,
  colorDict,
  categoryWeightDict as catWeightDict,
  categoryDict as catDict,
  categoryColorDict
} from '@constants/marktstatistiken'
import { localizedDecimal } from '@utils'
import { faCloudArrowUp } from '@fortawesome/pro-regular-svg-icons/faCloudArrowUp'
import { faEuroSign } from '@fortawesome/pro-solid-svg-icons/faEuroSign'
import { faPercent } from '@fortawesome/pro-solid-svg-icons/faPercent'
import { faBars } from '@fortawesome/pro-solid-svg-icons/faBars'
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage'

let categoryWeightDict, categoryDict, localeTexts

const sortFunction = (a, b) => {
  if (a.name in categoryWeightDict && b.name in categoryWeightDict) {
    return categoryWeightDict[b.name] - categoryWeightDict[a.name] || b.y - a.y
  } else {
    return b.y - a.y
  }
}

const getDrilldownSeries = (obj) => {
  const stack = [obj]
  const series = []
  let isFirstLevel = true
  const breakLimit = 5000
  let counter = 0
  while (stack?.length > 0) {
    counter += 1
    if (counter > breakLimit) {
      break
    }
    const currentObj = stack.pop()

    const id = currentObj.id ? currentObj.id : ''

    let objectToIterate = null
    if (!isFirstLevel) {
      const seriesObj = {
        id,
        name: currentObj.key,
        colorByPoint: true,
        showInLegend: true,
        data: Object.keys(currentObj.obj)
          .map((e) => {
            return {
              name: e,
              y: currentObj.obj[e].value,
              drilldown: id + '_' + e
            }
          })
          .sort(sortFunction)
          .filter((e) => e.name !== 'value')
      }

      series.push(seriesObj)

      objectToIterate = currentObj.obj
    } else {
      objectToIterate = currentObj
    }

    Object.keys(objectToIterate).forEach((key) => {
      if (
        typeof objectToIterate[key] === 'object' &&
        objectToIterate[key] !== null &&
        objectToIterate[key].value !== null &&
        Object.keys(objectToIterate[key]).length > 0 &&
        !(
          Object.keys(objectToIterate[key]).length === 1 &&
          'value' in objectToIterate[key]
        )
      ) {
        stack.push({
          id: id ? id + '_' + key : key,
          key,
          obj: objectToIterate[key]
        })
      }
    })
    isFirstLevel = false
  }
  return series
}

const logInterval = (totalIntervals, start, end) => {
  const x = (Math.log(end) - Math.log(start)) / totalIntervals
  const factor = Math.exp(x)
  const result = [start]

  for (let i = 1; i < totalIntervals; i++) {
    result.push(result[result.length - 1] * factor)
  }
  result.push(end)
  return result
}

const getColorArray = (steps, color, min, max) => {
  color = color.replace(')', ',')
  const colorArray = logInterval(steps, min, max)
    .map((e) => color + e.toString() + ')')
    .reverse()

  return colorArray
}

const aggregateData = (
  data,
  column,
  aggColumn,
  colorObject = null,
  filterColumn = null,
  filterValues = null,
  drilldownColumns = null,
  usecolorDict = false
) => {
  const aggregateDataDict = {}
  const drilldownAggregateDataDict = {}
  let totalFilterSum = 0
  let totalSum = 0
  for (const el of data) {
    if (filterColumn === null || filterValues.includes(el[filterColumn])) {
      if (el[column] in aggregateDataDict) {
        aggregateDataDict[el[column]] += el[aggColumn]
      } else {
        aggregateDataDict[el[column]] = el[aggColumn]
        drilldownAggregateDataDict[el[column]] = {}
      }
      if (drilldownColumns) {
        const drilldownValues = drilldownColumns.map((e) => el[e])
        drilldownValues.reduce(function (o, s) {
          o[s] = o[s]
            ? { ...o[s], value: o[s].value + el[aggColumn] }
            : { value: el[aggColumn] }
          return o[s]
        }, drilldownAggregateDataDict[el[column]])
      }
      totalFilterSum += el[aggColumn]
    }
    totalSum += el[aggColumn]
  }

  let resultData = Object.keys(aggregateDataDict)
    .map((key) => ({
      name: key,
      y: aggregateDataDict[key],
      drilldown: key
    }))
    .sort(sortFunction)

  const drilldownData = getDrilldownSeries(drilldownAggregateDataDict)

  if (colorObject) {
    if (resultData[0].name in categoryDict) {
      const lengthDict = Object.keys(categoryColorDict).reduce((a, e) => {
        return { ...a, [e]: 0 }
      }, {})
      const counterDict = Object.keys(categoryColorDict).reduce((a, e) => {
        return { ...a, [e]: 0 }
      }, {})
      const colorArrayDict = Object.keys(categoryColorDict).reduce((a, e) => {
        return { ...a, [e]: [] }
      }, {})

      for (const e of resultData) {
        lengthDict[categoryDict[e.name]] += 1
      }
      for (const key in colorArrayDict) {
        colorArrayDict[key] = getColorArray(
          lengthDict[key],
          categoryColorDict[key].color,
          categoryColorDict[key].min,
          categoryColorDict[key].max
        )
      }
      resultData = resultData.map((e) => {
        return {
          ...e,
          color:
            colorArrayDict[categoryDict[e.name]][
              counterDict[categoryDict[e.name]]++
            ]
        }
      })
    } else {
      const colorArray = getColorArray(
        resultData.length,
        colorObject.color,
        colorObject.min,
        colorObject.max
      )
      resultData = resultData.map((e, i) => {
        return {
          ...e,
          color:
            e.name in colorDict && usecolorDict
              ? colorDict[e.name]
              : colorArray[i]
        }
      })
    }

    for (let i = 0; i < drilldownData.length; i++) {
      if (drilldownData[i].data[0].name in categoryDict) {
        const lengthDict = Object.keys(categoryColorDict).reduce((a, e) => {
          return { ...a, [e]: 0 }
        }, {})
        const counterDict = Object.keys(categoryColorDict).reduce((a, e) => {
          return { ...a, [e]: 0 }
        }, {})
        const colorArrayDict = Object.keys(categoryColorDict).reduce((a, e) => {
          return { ...a, [e]: [] }
        }, {})
        for (const e of drilldownData[i].data) {
          lengthDict[categoryDict[e.name]] += 1
        }
        for (const key in colorArrayDict) {
          colorArrayDict[key] = getColorArray(
            lengthDict[key],
            categoryColorDict[key].color,
            categoryColorDict[key].min,
            categoryColorDict[key].max
          )
        }
        drilldownData[i].data = drilldownData[i].data.map((e) => {
          return {
            ...e,
            color:
              colorArrayDict[categoryDict[e.name]][
                counterDict[categoryDict[e.name]]++
              ]
          }
        })
      } else {
        const colorArray = getColorArray(
          drilldownData[i].data.length,
          colorObject.color,
          colorObject.min,
          colorObject.max
        )
        drilldownData[i].data = drilldownData[i].data.map((e, i) => {
          return {
            ...e,
            color:
              e.name in colorDict && usecolorDict
                ? colorDict[e.name]
                : colorArray[i]
          }
        })
      }
    }
  }

  return {
    filterSum: totalFilterSum,
    totalSum,
    drilldownData,
    data: resultData
  }
}

function useOutsideAlerter(ref, hideMenu) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        hideMenu()
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref])
}

const OwnCard = ({
  title,
  subtitle,
  chart,
  data,
  barData,
  uploadDate,
  refDate,
  info,
  column,
  aggColumn,
  filterColumn,
  filterValues,
  colorObject,
  colorArray,
  drilldownColumns,
  usecolorDict,
  seriesName,
  col1,
  col2,
  locale
}) => {
  const [showMenu, setShowMenu] = useState(false)
  const wrapperRef = useRef(null)
  const chartRef = useRef(null)

  const hideMenu = () => {
    setShowMenu(false)
  }

  const resultData = useMemo(
    () =>
      aggregateData(
        data,
        column,
        aggColumn,
        colorObject,
        filterColumn,
        filterValues,
        drilldownColumns,
        usecolorDict
      ),
    []
  )

  const [absValue, setAbsValue] = useState(
    localizedDecimal((resultData.filterSum / 1000000000).toFixed(1), locale) +
      ' ' +
      localeTexts.billions
  )
  const [relValue, setRelValue] = useState(
    localizedDecimal(
      ((resultData.filterSum / resultData.totalSum) * 100).toFixed(1),
      locale
    )
  )

  let difference = null
  let relDifference = null

  let xAxisCategories = null
  let seriesData = null

  const updateTextColumns = (absValue, relValue, topLevel = false) => {
    if (topLevel) {
      setAbsValue(
        localizedDecimal(
          (resultData.filterSum / 1000000000).toFixed(1),
          locale
        ) +
          ' ' +
          localeTexts.billions
      )
      setRelValue(
        localizedDecimal(
          ((resultData.filterSum / resultData.totalSum) * 100).toFixed(1),
          locale
        )
      )
    } else {
      setAbsValue(absValue)
      setRelValue(relValue)
    }
  }

  useOutsideAlerter(wrapperRef, hideMenu)

  const getDiffFromLastMonth = (data) => {
    const keys = Object.keys(data)
    keys.sort().reverse()
    let difference =
      data[keys[0]].Total[aggColumn] - data[keys[1]].Total[aggColumn]
    const relDifference = localizedDecimal(
      ((difference / data[keys[1]].Total[aggColumn]) * 100).toFixed(1),
      locale
    )
    difference =
      localizedDecimal((difference / 1000000000).toFixed(1), locale) +
      ' ' +
      localeTexts.billions
    return [difference, relDifference]
  }

  const getAxisData = (data) => {
    const keys = Object.keys(data)
    const xAxisCategories = keys.map((key) => {
      const dateString = new Date(key)
        .toLocaleDateString(locale === 'de' ? 'de-DE' : 'en-GB', {
          month: 'short',
          year: '2-digit'
        })
        .replace('.', '')
      const [month, year] = dateString.split(' ')
      return `${month.slice(0, 3)} ${year}`
    })

    const resultObject = {}
    const seriesData = []
    Object.values(data).forEach((date) => {
      Object.keys(date)
        .sort()
        .reverse()
        .forEach((exchange) => {
          if (exchange !== 'Total') {
            if (exchange in resultObject) {
              resultObject[exchange].push(date[exchange][aggColumn])
            } else {
              resultObject[exchange] = [date[exchange][aggColumn]]
            }
          }
        })
    })

    Object.keys(resultObject).forEach((exchange) => {
      let exchangeName = exchange
      if (locale === 'en') {
        if (exchange.startsWith('SRI-')) {
          exchangeName = exchange.replace('SRI-', 'SRI ')
        } else if (exchangeName === texts.de.total) {
          exchangeName = localeTexts.total
        }
      }
      seriesData.push({
        name: exchangeName,
        color: colorDict[exchange],
        data: resultObject[exchange],
        stack: 'boersenumsaetze'
      })
    })

    return [xAxisCategories, seriesData]
  }

  if (chart === 'bar') {
    ;[difference, relDifference] = getDiffFromLastMonth(barData)
    const result = getAxisData(barData)
    xAxisCategories = result[0]
    seriesData = result[1]
  }

  if (locale === 'en') {
    Object.keys(resultData.data).forEach((e) => {
      let exchangeName = resultData.data[e].name
      if (exchangeName.startsWith('SRI-')) {
        exchangeName = exchangeName.replace('SRI-', 'SRI ')
      }
      resultData.data[e].name = exchangeName
    })

    const [uploadDay, uploadMonth, uploadYear] = uploadDate.split('.')
    const [refDay, refMonth, refYear] = refDate.split('.')

    const uploadDateFormatted = new Date(
      `${uploadYear}-${uploadMonth}-${uploadDay}`
    ).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
    const refDateFormatted = new Date(
      `${refYear}-${refMonth}-${refDay}`
    ).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })

    uploadDate = uploadDateFormatted
    refDate = refDateFormatted
  }

  return (
    <Card style={{ height: '97%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'right',
          alignItems: 'right',
          position: 'relative'
        }}
      >
        <MenuButton onClick={() => setShowMenu(true)}>
          <FontAwesomeIcon icon={faBars} />
        </MenuButton>
        {showMenu && (
          <Menu ref={wrapperRef}>
            <MenuItem
              onClick={() =>
                chartRef.current?.exportChartLocal({ type: 'image/png' })
              }
            >
              <FontAwesomeIcon
                style={{ paddingRight: 10, color: 'rgb(181 90 48)' }}
                icon={faImage}
              />
              {localeTexts.download.png}
            </MenuItem>
            <MenuItem
              onClick={() =>
                chartRef.current?.exportChartLocal({ type: 'image/jpeg' })
              }
            >
              <FontAwesomeIcon
                style={{ paddingRight: 10, color: 'rgb(181 90 48)' }}
                icon={faImage}
              />
              {localeTexts.download.jpeg}
            </MenuItem>
            <MenuItem
              onClick={() =>
                chartRef.current?.exportChartLocal({ type: 'image/svg+xml' })
              }
            >
              <FontAwesomeIcon
                style={{ paddingRight: 10, color: 'rgb(181 90 48)' }}
                icon={faImage}
              />
              {localeTexts.download.svg}
            </MenuItem>
            {/* <MenuItem onClick={() => chartRef.current?.fullscreen.open()}><i style={{ paddingRight: 10, color: 'rgb(181 90 48)' }} class='fas fa-expand-alt' />Vollbild-Modus</MenuItem> */}
          </Menu>
        )}
      </div>
      <Row style={{ position: 'relative' }}>
        <CardHeader>
          <span style={{ marginRight: 10 }}>{title}</span>
          {info && (
            <span style={{ whiteSpace: 'nowrap' }}>
              <MenuInfo>{info}</MenuInfo>
            </span>
          )}
        </CardHeader>
        <Col
          lg={6}
          style={{ borderRight: '2px solid lightgrey', marginBottom: '20px' }}
        >
          {chart === 'pie' && (
            <CardText>
              <span
                style={{ fontSize: '20px', fontWeight: 700, color: '#b55a30' }}
              >
                <FontAwesomeIcon icon={faEuroSign} /> {absValue}{' '}
              </span>
              <br /> {col1}{' '}
            </CardText>
          )}
          {chart === 'bar' && (
            <CardText>
              <span
                style={{ fontSize: '20px', fontWeight: 700, color: '#b55a30' }}
              >
                <FontAwesomeIcon icon={faEuroSign} /> {difference}{' '}
              </span>
              <br /> {col1}{' '}
            </CardText>
          )}
        </Col>
        <Col lg={6} style={{ marginBottom: 20 }}>
          {chart === 'pie' && (
            <CardText>
              <span
                style={{ fontSize: '20px', fontWeight: 700, color: '#b55a30' }}
              >
                <FontAwesomeIcon icon={faPercent} /> {relValue}
              </span>
              <br /> {col2}{' '}
            </CardText>
          )}
          {chart === 'bar' && (
            <CardText>
              <span
                style={{ fontSize: '20px', fontWeight: 700, color: '#b55a30' }}
              >
                <FontAwesomeIcon icon={faPercent} /> {relDifference}
              </span>
              <br /> {col2}{' '}
            </CardText>
          )}
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          {chart === 'bar' && (
            <BarChart
              chartRef={chartRef}
              xAxisCategories={xAxisCategories}
              seriesData={seriesData}
              title={subtitle}
              locale={locale}
            />
          )}
          {chart === 'pie' && (
            <PieChart
              chartRef={chartRef}
              seriesName={seriesName}
              updateTextColumns={updateTextColumns}
              colorArray={colorArray}
              title={subtitle}
              subtitle=""
              data={resultData.data}
              drilldownData={resultData.drilldownData}
              drilldownColumns={resultData.drilldownData}
              column={column}
              aggColumn={aggColumn}
              filterColumn={filterColumn}
              filterValues={filterValues}
              locale={locale}
            />
          )}
        </Col>
      </Row>
      <CardFooter>
        <FontAwesomeIcon icon={faCloudArrowUp} /> {uploadDate},{' '}
        {localeTexts.asOf}: {refDate}{' '}
        <span style={{ float: 'right' }}>{localeTexts.source}</span>
      </CardFooter>
    </Card>
  )
}

const MarktvolumenArea = ({
  dataAccordion,
  dataCards,
  dataCurrent,
  dataArchive,
  ...rest
}) => {
  const [bswData, setBswData] = useState(null)
  const [uploadDate, setUploadDate] = useState('')
  const [refDate, setRefDate] = useState('')
  const [bswSummary, setBswSummary] = useState(null)
  const [loading, setLoading] = useState(true)

  const { locale } = usePageContext()

  categoryWeightDict = catWeightDict[locale] || catWeightDict.de
  categoryDict = catDict[locale] || catDict.de
  localeTexts = texts[locale] || texts.de

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`/api/data/marktvolumen?locale=${locale}`)
        if (!res.ok) {
          throw new Error('Error fetching marktvolumen data')
        }
        const r = await res.json()
        setBswData(r.bswData)
        setBswSummary(r.bswSummary)
        setUploadDate(r.uploadDate)
        setRefDate(r.refDate)
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const items = dataCards?.items?.map((e) => {
    e.texts.forEach((t) => {
      const [key, value] = t.content.split(':')
      e[key] = value
    })
    return e
  })

  return (
    <SectionWrapper {...rest}>
      <Container>
        <StyledText>
          <Accordion
            iconAtEnd={false}
            disableDefaultActive
            colBreakpoints={{ xs: 6 }}
            data={dataAccordion.items}
          />
        </StyledText>
        <StyledText style={{ marginTop: '80px' }}>
          <Row>
            {loading && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100px',
                  color: '#00408a',
                  marginBottom: '50px'
                }}
              >
                <Spinner animation="border" />
              </div>
            )}
            {!loading && !bswData && !bswSummary && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100px',
                  color: '#00408a',
                  marginBottom: '50px'
                }}
              >
                <span>{localeTexts.noData}</span>
              </div>
            )}
            {bswData &&
              bswSummary &&
              items?.map((card, index) => (
                <Col key={index} lg={6}>
                  <OwnCard
                    title={card.title}
                    subtitle={card.description}
                    chart={card.chart}
                    col1={card.col1}
                    col2={card.col2}
                    info={card.info}
                    data={bswData}
                    barData={bswSummary}
                    uploadDate={uploadDate}
                    refDate={refDate}
                    seriesName={card.name}
                    column={card.column}
                    aggColumn={card.aggColumn}
                    drilldownColumns={card.drilldownColumns?.split(',')}
                    filterValues={card.filterValues?.split(',')}
                    filterColumn={card.filterColumn}
                    colorObject={
                      card.color
                        ? { color: card.color, min: 0.1, max: 1 }
                        : null
                    }
                    usecolorDict
                    locale={locale}
                  />
                </Col>
              ))}
          </Row>
        </StyledText>

        {dataCurrent?.texts?.[0] && (
          <StyledText
            style={{ marginTop: '50px' }}
            dangerouslySetInnerHTML={{
              __html: dataCurrent.texts[0].content
            }}
          />
        )}
        <DocumentArchive dataCurrent={dataCurrent} dataArchive={dataArchive} />
        {dataArchive?.texts?.[0] && (
          <StyledText
            style={{ marginTop: '60px' }}
            dangerouslySetInnerHTML={{
              __html: dataArchive.texts[0].content
            }}
          />
        )}
      </Container>
    </SectionWrapper>
  )
}

export default MarktvolumenArea
